import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { setLastRequestId, captureException, scrubPath } from '../observability';
import { errorMessage } from './errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'loa_token';

export const setToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Login-as state (ticket 73). Lives here, next to the token, so the revoked-
// session path below can clear it without importing ./impersonation (which
// imports this module). Everything else goes through ./impersonation.
export const IMPERSONATION_KEY = 'impersonating';
const IMPERSONATION_KEYS = [IMPERSONATION_KEY, 'loa_original_token', 'loa_original_email'];
export const clearImpersonation = () => IMPERSONATION_KEYS.forEach((k) => localStorage.removeItem(k));

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// A matrix denial (permissions.py `check`) is the one 403 the user can act
// on: surface the backend's message so they can relay the permission name
// to their workspace admin. Everything else (404 on an unassigned deal,
// other 403s) keeps its existing, deliberately opaque handling.
export function permissionDeniedMessage(error) {
  const d = error?.response?.status === 403 ? error?.response?.data?.detail : null;
  return d && d.code === 'permission_denied' ? d.message : null;
}

// Ticket 205: a suspended workspace answers every call with 403 `tenant_suspended`.
export function tenantSuspendedMessage(error) {
  const d = error?.response?.status === 403 ? error?.response?.data?.detail : null;
  return d && d.code === 'tenant_suspended' ? d.message : null;
}
let lastSuspendedToastAt = -Infinity;
function shouldToastSuspended(now = Date.now()) {
  if (now - lastSuspendedToastAt < 5000) return false;
  lastSuspendedToastAt = now;
  return true;
}

// Ticket 167: a tenant that is not yet approved gets 403 `spend_approval_required`
// on every feature that costs money. The copy is fixed (not the server's
// message) so the toast, the banner and the form all say the same thing.
export const SPEND_PENDING_COPY = 'Awaiting approval, usually within one business day.';
export const SPEND_DENIED_COPY = 'AI, texting, email and e-signature are off for this workspace. Contact support.';

export function isSpendApprovalError(error) {
  if (error?.spendApproval) return true;
  const d = error?.response?.status === 403 ? error?.response?.data?.detail : null;
  return !!d && d.code === 'spend_approval_required';
}

export function spendApprovalMessage(error) {
  if (!isSpendApprovalError(error)) return null;
  if (error.spendApproval) return error.spendApproval.message;
  return error.response.data.detail.status === 'denied' ? SPEND_DENIED_COPY : SPEND_PENDING_COPY;
}

// A page can fire several gated calls at once; show one toast per window.
const SPEND_TOAST_WINDOW_MS = 5000;
let lastSpendToastAt = -Infinity;
export function shouldToastSpendApproval(now = Date.now()) {
  if (now - lastSpendToastAt < SPEND_TOAST_WINDOW_MS) return false;
  lastSpendToastAt = now;
  return true;
}
export const _resetSpendToastGuard = () => { lastSpendToastAt = -Infinity; };

// Incident kill switch (ticket 45): the backend answers 401 'session_revoked'
// once an admin has signed this account out everywhere. Drop the token and
// send the tab to login so a killed session can't keep a live UI.
function isSessionRevoked(error) {
  return error?.response?.status === 401 && error?.response?.data?.detail === 'session_revoked';
}

// Ticket 196: "Sign out my other devices" revokes this tab's token too and
// returns its replacement. A request already in flight with the old token
// (the inbox poll, the bell) can come back `session_revoked` around the swap,
// in this tab or another one sharing localStorage. That 401 is about the old
// token and must not throw away the new one.
let tokenSwapInFlight = false;
function sentWithStaleToken(error) {
  const sent = error?.config?.headers?.Authorization;
  const current = getToken();
  return Boolean(sent && current && sent !== `Bearer ${current}`);
}

// A call that revokes every older token of this user answers with a
// replacement; swap it in before the next request so this device stays in.
async function postAndSwapToken(url, body) {
  tokenSwapInFlight = true;
  try {
    const { data } = await api.post(url, body);
    if (data?.access_token) setToken(data.access_token);
    return data;
  } finally {
    tokenSwapInFlight = false;
  }
}

export const signOutOtherDevices = () => postAndSwapToken('/auth/sign-out-others');

// Ticket 196: the server also signs out every other device, like a reset.
export const changePassword = (currentPassword, newPassword) =>
  postAndSwapToken('/auth/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
  });

// Toast the two 403s the user can act on (matrix denial, spend gate) and
// normalise a spend-gate error. Returns true when the error was one of them.
// Shared with the file helpers below, which only see a decoded error body.
function notifyGateDenial(error) {
  const suspended = tenantSuspendedMessage(error);
  if (suspended && shouldToastSuspended()) {
    toast({ variant: 'blocked', title: 'Workspace suspended', description: suspended });
  }
  if (suspended) error.gateNotified = true;
  const msg = permissionDeniedMessage(error);
  if (msg) toast({ variant: 'blocked', title: 'Permission needed', description: msg });
  const spendMsg = spendApprovalMessage(error);
  if (spendMsg && shouldToastSpendApproval()) {
    const denied = (error.spendApproval || error.response.data.detail).status === 'denied';
    toast({ variant: 'blocked', title: denied ? 'Workspace not approved' : 'Workspace not approved yet', description: spendMsg });
  }
  if (spendMsg && !error.spendApproval) {
    // Many callers read `detail` as a string; hand them the message so a
    // gated failure can't crash a toast or a .includes/.slice call.
    error.spendApproval = { status: error.response.data.detail.status, message: spendMsg };
    error.response.data.detail = spendMsg;
  }
  if (msg || spendMsg) error.gateNotified = true;
  return !!(suspended || msg || spendMsg);
}

// A blob request (ticket 185) carries its JSON error body as a Blob. Decode it
// in place so the checks below (gate toasts, session_revoked) can read it.
async function decodeBlobBody(error) {
  const body = error?.response?.data;
  if (!(body instanceof Blob)) return;
  try { error.response.data = JSON.parse(await body.text()); } catch { error.response.data = {}; }
}

function handleResponseError(error) {
  setLastRequestId(error.response?.headers?.['x-request-id']);
  notifyGateDenial(error);
  if (isSessionRevoked(error) && !tokenSwapInFlight && !sentWithStaleToken(error) && typeof window !== 'undefined') {
    setToken(null);
    clearImpersonation();
    if (!window.location.pathname.startsWith('/portal/login')) {
      toast({ title: 'Signed out', description: 'Your session was ended. Please sign in again.' });
      window.location.assign('/portal/login');
    }
  }
  // Report unexpected server errors — never the request body/headers, just
  // a synthetic message with the method + path (ticket 123 F6).
  const status = error.response?.status;
  if (status >= 500) {
    const method = (error.config?.method || 'GET').toUpperCase();
    const pathOnly = (() => {
      try { return scrubPath(new URL(error.config?.url, window.location.origin).pathname); } catch { return error.config?.url; }
    })();
    captureException(new Error(`API ${status} ${method} ${pathOnly}`), {
      tags: { kind: 'api_5xx', request_id: error.response?.headers?.['x-request-id'] },
    });
  }
  return Promise.reject(error);
}

// Ticket 123: every backend response carries X-Request-ID; keep the latest
// one so the error boundary has a reference even without a Sentry event id.
api.interceptors.response.use(
  (res) => {
    setLastRequestId(res.headers?.['x-request-id']);
    return res;
  },
  (error) => (error?.response?.data instanceof Blob
    ? decodeBlobBody(error).then(() => handleResponseError(error))
    : handleResponseError(error)),
);

// Ticket 185: the API reads the login token from `Authorization: Bearer`, which
// a plain link, window.open or <img src> cannot send (and a `?auth=` token in a
// URL lands in history, logs and referrers). These fetch the file through the
// axios instance, header attached, and give the browser a blob: URL instead.
const getBlob = (path) => api.get(path, { responseType: 'blob' });

export const fetchBlob = (path) => getBlob(path).then((res) => res.data);

function fileNameFromDisposition(header) {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim()); } catch { /* fall through to plain */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

// Toast a failed file request with the same copy as any other failed call. The
// response interceptor has already handled the 403 gates and a revoked session.
async function toastFileError(error) {
  await decodeBlobBody(error);
  if (isSessionRevoked(error)) return;
  if (error.gateNotified || notifyGateDenial(error)) return;
  toast({ title: 'Could not load the file', description: errorMessage(error) });
}

// One save path for both helpers: a click on a throwaway link to a blob URL.
function saveResponse(res, fallbackName) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileNameFromDisposition(res.headers?.['content-disposition']) || fallbackName || 'download';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadWithAuth(path, fallbackName) {
  try {
    saveResponse(await getBlob(path), fallbackName);
  } catch (error) {
    await toastFileError(error);
  }
}

// A blob: URL runs in this app's origin, where the login token lives. So a tab
// only shows types that cannot run script; anything else (html, svg, docx,
// csv, an unknown type) is saved as a file instead.
const TAB_SAFE_TYPES = new Set([
  'application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'text/plain',
]);

const bareType = (type) => (type || '').split(';')[0].trim().toLowerCase();
const blobType = (blob) => bareType(blob?.type);

// For callers that know the type up front and can skip the blank tab.
export const isTabSafeType = (type) => TAB_SAFE_TYPES.has(bareType(type));

// `allowHtml` is for pages this app's own backend builds from escaped data
// (the contract preview, which also carries a CSP). Never for stored uploads.
export async function openWithAuth(path, { allowHtml = false, fallbackName } = {}) {
  // Open the tab inside the click, before the fetch, so popup blockers allow it.
  const tab = window.open('', '_blank');
  if (!tab) {
    toast({ title: 'Popup blocked', description: 'Allow popups for this site, then try again.' });
    return;
  }
  try {
    const res = await getBlob(path);
    const type = blobType(res.data);
    if (TAB_SAFE_TYPES.has(type) || (allowHtml && type === 'text/html')) {
      // Not revoked: the tab is showing this URL and a reload would blank it.
      tab.location.href = URL.createObjectURL(res.data);
    } else {
      tab.close();
      saveResponse(res, fallbackName);
    }
  } catch (error) {
    tab.close();
    await toastFileError(error);
  }
}

export { api, API, BACKEND_URL };
